import { Button, Container, Theme, Typography } from "@mui/material";
import {
  Brand,
  ButtonVariant,
  Domains,
  HeadingLevel,
} from "@/core/types/types";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import Cookies from "js-cookie";
import { useContext } from "react";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { getDomainData } from "@/core/utils/getBrandConfiguration";

const styles = (theme: Theme) => ({
  root: {
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    maxWidth: "456px",
  },
  subtitle: {
    maxWidth: "456px",
    paddingBottom: "10px",
    fontWeight: 400,
  },
  buttons: {
    width: "100%",
    maxWidth: "456px",
    textTransform: "inherit",
  },
});

interface NotFoundUIProps {}

const NotFoundUI = ({}: NotFoundUIProps) => {
  const classes: any = useCustomStyles(styles);
  const { flush } = useClaimCheck();
  const env: "prod" | "stg" =
    process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === "prod" ? "prod" : "stg";

  const initializationConfig = useContext(ConfigContext);

  const { brandFamily, domain } = initializationConfig;

  const cleanCookiesAndRedirectToCustomerPortal = (): void => {
    const customerPortalURLs =
      getDomainData(brandFamily, initializationConfig.legalArea, domain)
        ?.CustomerPortalURL ||
      getDomainData(
        Brand.FLIGHTRIGHT,
        initializationConfig.legalArea,
        Domains.DE
      )?.CustomerPortalURL;

    flush().then(() => {
      if (typeof window !== "undefined") {
        Cookies.remove("process");
        if (customerPortalURLs) {
          window.location.href = customerPortalURLs[env];
        }
      }
    });
  };

  const handleRestartFlow = (): void => {
    flush().then(() => {
      if (typeof window !== "undefined") {
        Cookies.remove("process");
        window.location.reload();
      }
    });
  };

  return (
    <Container sx={classes?.root}>
      <Typography variant={HeadingLevel.H1}>{t("notFound.title")}</Typography>
      <Typography variant={HeadingLevel.Body2} sx={classes?.subtitle}>
        {t("notFound.subtitle")}
      </Typography>
      <>
        <Button
          sx={classes?.buttons}
          type="submit"
          onClick={handleRestartFlow}
          variant={ButtonVariant.Contained}
        >
          {t("error.btn.restart.label")}
        </Button>
        <Button
          sx={classes?.buttons}
          type="submit"
          onClick={cleanCookiesAndRedirectToCustomerPortal}
          variant={ButtonVariant.Outlined}
        >
          {t("notFound.button.redirect.title")}
        </Button>
      </>
    </Container>
  );
};

export { NotFoundUI };
